import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useOperatorService } from "../../api/operatorService";
import { useSnackbar } from "../../contexts/snackbar-context";
import { Operator } from "../../types/operator";
import { ConfirmDialog } from "../common/confirm-dialog";
import {
  tenantManagementAccordionSummarySx,
  tenantManagementAccordionSx,
  tenantManagementDialogPaperSx,
  tenantManagementPaperSx,
  tenantManagementPrimaryButtonSx,
  tenantManagementTableBodyCellSx,
  tenantManagementTableHeadCellSx,
  tenantManagementTextFieldSx,
} from "./tenantManagementStyles";

type EditorState = {
  open: boolean;
  isEdit: boolean;
  id?: string;
  name: string;
};

const defaultEditorState: EditorState = {
  open: false,
  isEdit: false,
  name: "",
};

type OperatorsTabProps = {
  active: boolean;
};

export const OperatorsTab: React.FC<OperatorsTabProps> = ({ active }) => {
  const { showSuccess, showError } = useSnackbar();
  const {
    getOperators,
    createOperator,
    updateOperator,
    deactivateOperator,
    activateOperator,
  } = useOperatorService();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [editor, setEditor] = useState<EditorState>(defaultEditorState);
  const [deactivateTarget, setDeactivateTarget] = useState<Operator | null>(null);
  const [activateTarget, setActivateTarget] = useState<Operator | null>(null);

  const loadOperators = useCallback(async () => {
    setLoading(true);
    try {
      const items = await getOperators(false);
      setOperators(items);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load operators.";
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [getOperators, showError]);

  useEffect(() => {
    if (active) {
      void loadOperators();
    }
  }, [active, loadOperators]);

  const grouped = useMemo(() => {
    const activeOps = operators
      .filter((o) => o.isActive)
      .sort((a, b) => a.name.localeCompare(b.name));
    const inactiveOps = operators
      .filter((o) => !o.isActive)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { activeOps, inactiveOps };
  }, [operators]);

  const handleOpenAdd = () => {
    setEditor({ ...defaultEditorState, open: true });
  };

  const handleOpenEdit = (item: Operator) => {
    setEditor({
      open: true,
      isEdit: true,
      id: item.id,
      name: item.name,
    });
  };

  const handleCloseEditor = () => {
    if (saving) return;
    setEditor(defaultEditorState);
  };

  const handleSave = async () => {
    const name = editor.name.trim();
    if (!name) {
      showError("Operator name is required.");
      return;
    }


    setSaving(true);
    try {
      if (editor.isEdit && editor.id) {
        await updateOperator(editor.id, { name });
        showSuccess("Operator updated");
      } else {
        await createOperator({ name });
        showSuccess("Operator added");
      }
      setEditor(defaultEditorState);
      await loadOperators();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save operator.";
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;

    setSaving(true);
    try {
      await deactivateOperator(deactivateTarget.id);
      setDeactivateTarget(null);
      showSuccess("Operator deactivated");
      await loadOperators();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to deactivate operator.";
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmActivate = async () => {
    if (!activateTarget) return;

    setSaving(true);
    try {
      await activateOperator(activateTarget.id);
      setActivateTarget(null);
      showSuccess("Operator activated");
      await loadOperators();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to activate operator.";
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const renderOperatorTable = (
    items: Operator[],
    actions: "active" | "inactive" | "none"
  ) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tenantManagementTableHeadCellSx}>Name</TableCell>
            <TableCell sx={tenantManagementTableHeadCellSx}>Created</TableCell>
            {actions !== "none" && (
              <TableCell
                sx={{ ...tenantManagementTableHeadCellSx, width: 120 }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.id}
              sx={{ "&:hover": { bgcolor: "#1a1a1a" } }}
            >
              <TableCell sx={tenantManagementTableBodyCellSx}>
                {item.name}
              </TableCell>
              <TableCell sx={tenantManagementTableBodyCellSx}>
  {item.createdAt?.split(" ")[0]}
</TableCell>
              {actions === "active" && (
                <TableCell sx={{ borderColor: "#333" }}>
                  <IconButton
                    size="small"
                    sx={{ color: "hsl(19 101% 51% / 1)", mr: 0.5 }}
                    onClick={() => handleOpenEdit(item)}
                    aria-label={`Edit ${item.name}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: "#f44336" }}
                    onClick={() => setDeactivateTarget(item)}
                    aria-label={`Deactivate ${item.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
              {actions === "inactive" && (
                <TableCell sx={{ borderColor: "#333" }}>
                  <IconButton
                    size="small"
                    sx={{ color: "hsl(19 101% 51% / 1)", mr: 0.5 }}
                    onClick={() => handleOpenEdit(item)}
                    aria-label={`Edit ${item.name}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: "hsl(142 52% 62%)" }}
                    onClick={() => setActivateTarget(item)}
                    aria-label={`Activate ${item.name}`}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccordionSection = (
    sectionKey: string,
    title: string,
    items: Operator[],
    defaultExpanded: boolean,
    actions: "active" | "inactive" | "none"
  ) => {
    if (items.length === 0) return null;

    return (
      <Accordion
        key={sectionKey}
        defaultExpanded={defaultExpanded}
        sx={tenantManagementAccordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "hsl(19 101% 51% / 1)" }} />}
          sx={tenantManagementAccordionSummarySx}
        >
          <Typography
            sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 600, flex: 1 }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: "#999", mr: 2 }}>({items.length})</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {renderOperatorTable(items, actions)}
        </AccordionDetails>
      </Accordion>
    );
  };

  const hasAnyOperators =
    grouped.activeOps.length > 0 || grouped.inactiveOps.length > 0;

  return (
    <>
      <Paper sx={tenantManagementPaperSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
          spacing={2}
        >
          <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)" }}>
            Operators
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={tenantManagementPrimaryButtonSx}
          >
            Add Operator
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !hasAnyOperators ? (
          <Typography sx={{ color: "#999", textAlign: "center", py: 4 }}>
            No operators configured.
          </Typography>
        ) : (
          <Stack spacing={0}>
            {renderAccordionSection(
              "active",
              "Active Operators",
              grouped.activeOps,
              true,
              "active"
            )}
            {renderAccordionSection(
              "inactive",
              "Inactive Operators",
              grouped.inactiveOps,
              false,
              "inactive"
            )}
          </Stack>
        )}
      </Paper>

      <Dialog
        open={editor.open}
        onClose={handleCloseEditor}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: tenantManagementDialogPaperSx }}
      >
        <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
          {editor.isEdit ? "Edit Operator" : "Add Operator"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={editor.name}
              onChange={(e) =>
                setEditor((prev) => ({ ...prev, name: e.target.value }))
              }
              inputProps={{ maxLength: 200 }}
              sx={tenantManagementTextFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditor}
            sx={{ color: "#fff" }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={tenantManagementPrimaryButtonSx}
          >
            {saving ? "Saving..." : editor.isEdit ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate Operator"
        message={
          deactivateTarget
            ? `Deactivate operator "${deactivateTarget.name}"? It cannot be used on new assignments while inactive.`
            : ""
        }
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        isDangerous
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        isLoading={saving}
      />

      <ConfirmDialog
        open={!!activateTarget}
        title="Activate Operator"
        message={
          activateTarget
            ? `Activate operator "${activateTarget.name}"? It will be available for new assignments.`
            : ""
        }
        confirmLabel="Activate"
        cancelLabel="Cancel"
        onConfirm={handleConfirmActivate}
        onCancel={() => setActivateTarget(null)}
        isLoading={saving}
      />
    </>
  );
};
